import { cn } from '#app/utils/misc.tsx'

export type EditorialItem = {
	/** Large decorative mark — a letter, a number, an icon. Renders ghosted. */
	mark: React.ReactNode
	eyebrow?: string
	title: React.ReactNode
	stats?: string[]
	accessory?: React.ReactNode
	body: React.ReactNode
}

type Props = {
	items: EditorialItem[]
	className?: string
}

/**
 * Editorial row list. Each row is a 12-column band:
 *
 * - left 2 cols  → big ghosted decorative `mark`
 * - middle 5 cols → eyebrow, title, stats, accessory
 * - right 5 cols → body prose
 *
 * Rows are separated by hairlines, not boxed in cards — gives the page an
 * editorial rhythm rather than a templated card-grid feel.
 *
 * Use for: glossary-style overviews, comparison rows, multi-class listings.
 */
export function EditorialList({ items, className }: Props) {
	return (
		<div className={cn('', className)}>
			{items.map((item, i) => (
				<article
					key={i}
					className={cn(
						'border-border grid gap-6 border-t py-8 md:grid-cols-12 md:gap-10 md:py-12',
						i === items.length - 1 && 'border-b',
					)}
				>
					<div className="md:col-span-2">
						<div
							aria-hidden
							className="text-primary/15 text-7xl leading-none font-black tracking-tight md:text-8xl"
						>
							{item.mark}
						</div>
					</div>

					<div className="md:col-span-5">
						{item.eyebrow ? (
							<div className="text-muted-foreground font-mono text-xs font-semibold tracking-widest uppercase">
								{item.eyebrow}
							</div>
						) : null}
						<h3 className="text-foreground mt-1 text-2xl font-bold tracking-tight md:text-3xl">
							{item.title}
						</h3>
						{item.stats && item.stats.length > 0 ? (
							<dl className="text-foreground/80 mt-4 flex flex-wrap gap-x-5 gap-y-1 text-sm">
								{item.stats.map((s) => (
									<dt key={s} className="font-medium">
										{s}
									</dt>
								))}
							</dl>
						) : null}
						{item.accessory ? <div className="mt-5">{item.accessory}</div> : null}
					</div>

					<div className="md:col-span-5">
						<div className="text-foreground/85 text-base leading-relaxed">
							{item.body}
						</div>
					</div>
				</article>
			))}
		</div>
	)
}
